import TF from "@/components/forms/TextField.module.css";

import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WNumberField } from "@/components/forms/WNumberField.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { addSubInterfaceMutation } from "@/modules/compute/vmInterfacesQuery.ts";
import { VpcNetworkSelect } from "@/modules/network/VpcNetworkSelect.tsx";
import { cn } from "@/utils/classNames.ts";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Input } from "react-aria-components";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

interface SubInterfaceType {
    uuid: string;
    vmiUuid: string;
    networkUuid: string;
    location: string;
}

interface AddSubInterfaceType extends SubInterfaceType {
    button: ReactNode;
}

interface AddSubInterfaceInputs {
    vlanTag: number;
    networkUuid: string;
}

export function AddSubInterfaceButton({ uuid, vmiUuid, networkUuid, location, button }: AddSubInterfaceType) {
    return (
        <WModal button={button}>
            <AddSubInterfaceModalContent location={location} uuid={uuid} vmiUuid={vmiUuid} networkUuid={networkUuid} />
        </WModal>
    );
}

function AddSubInterfaceModalContent({ uuid, vmiUuid, networkUuid, location }: SubInterfaceType) {
    //#region Hooks
    const { t } = useLingui();
    const createMutation = useStandardMutation(addSubInterfaceMutation);
    //#endregion

    const onSubmit: SubmitHandler<AddSubInterfaceInputs> = async (inputs) => {
        const { vlanTag, networkUuid } = inputs;

        await createMutation.mutateAsync({
            location,
            uuid,
            ifUuid: vmiUuid,
            networkUuid,
            vlanTag,
        });
    };

    const form = useForm<AddSubInterfaceInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            vlanTag: NaN,
            networkUuid: networkUuid,
        },
    });

    const { control } = form;

    return (
        <WModalForm title={t`Add sub-interface`} label={t`Add`} form={form} onSubmit={onSubmit}>
            <Controller
                control={control}
                name="vlanTag"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WNumberField
                        label={t`VLAN ID`}
                        description={t`Enter a value between 1 and 4094`}
                        autoFocus
                        headless
                        isRequired
                        className={cn(TF.NumberField)}
                        minValue={1}
                        maxValue={4094}
                        aria-label="VLAN ID"
                        formatOptions={{
                            maximumFractionDigits: 0,
                            signDisplay: "never",
                        }}
                        {...formPropsToAriaProps(props)}
                    >
                        <Input className={TF.Input} />
                    </WNumberField>
                )}
            />

            <FormField isRequired wide headless label={t`VPC network`}>
                <Controller
                    control={control}
                    name="networkUuid"
                    {...controllerProps({ isRequired: true })}
                    render={({ field }) => (
                        <VpcNetworkSelect
                            isRequired
                            location={location}
                            isDisabled={field.disabled}
                            valueKey={field.value ?? null}
                            onChange={(_vpc, key) => field.onChange(key)}
                        />
                    )}
                />
            </FormField>
        </WModalForm>
    );
}
